"use client"
import NumberFlow from "@number-flow/react"
import { useEffect, useLayoutEffect, useRef } from "react"
import { TICKER_NUMBER_FLOW_TIMING } from "../../indicators/TickerNumber"
import { useHudDates } from "../shared/playbookHUDInfra"
import { useMetricDetails } from "./metricDetailsContext"

// THE year pill while a metric-details state is open — one real element, no
// clones. The graph's own pill lives inside the variant layer, and the panel
// header lives inside the details layer; both layers opacity-fade during the
// open/close handoff, so neither copy could stay visible while moving. This
// pill renders in the persistent overlay BETWEEN the layers instead: on open
// it appears exactly over the graph's pill (which hides underneath it for
// the whole open state), glides to the panel header's invisible placeholder
// ([data-metric-panel-year]), and stays glued to it through panel scrolling
// and resizes; on close it glides home and hands visibility back on the
// same pixels. Fully opaque the whole way — the position of the actual pill
// animates; nothing is duplicated.
//
// The pill has two looks since the graph pill's title-tier restyle: docked
// on the graph it matches the graph pill (14px, 2.5px/9px padding, 6px
// radius — see the data-hud-year-pill styling in ExtrapolatedGraphRenderer);
// docked on the panel header it matches PanelYearReadout (17px, 3px/11px
// padding, 8px radius). Each glide morphs font-size, padding and radius
// alongside the transform, so the pill grows into the panel tier on open and
// shrinks home on close. The digits are the same NumberFlow at the same
// timings, so the year keeps spinning with scroll mid-glide and while docked.
//
// Mechanics: the span is ALWAYS mounted; show/hide/position are imperative
// in one layout effect (no delayed-unmount state to race), and glides run
// as WAAPI animations over a style.transform base that always holds the
// current TARGET pose — compositor-driven, so re-renders and main-thread
// jank can't stall or restart them. Every handoff step is idempotent.

const DOCK_MS = 340 // spans the sequential fade: 140ms leave + 200ms arrive
const DOCK_EASING = "cubic-bezier(0.22, 1, 0.36, 1)"
// The panel header can mount a frame or two after openId flips — poll a few
// frames for the placeholder before giving up.
const PLACEHOLDER_POLL_FRAMES = 12

// The two docked looks (see the header comment) — inline-style tiers, not
// classes, because each glide animates BETWEEN them via WAAPI. Keep
// GRAPH_TIER in sync with the graph pill's styling in
// ExtrapolatedGraphRenderer, and PANEL_TIER with PanelYearReadout.
type PillTier = { fontSize: string; padding: string; borderRadius: string }
const GRAPH_TIER: PillTier = { fontSize: "14px", padding: "2.5px 9px", borderRadius: "6px" }
const PANEL_TIER: PillTier = { fontSize: "17px", padding: "3px 11px", borderRadius: "8px" }

export const DetailsYearPill = () => {
  const { openId } = useMetricDetails()
  const open = openId !== null
  const { targetDate } = useHudDates()
  const year = Math.floor(targetDate.getFullYear() + targetDate.getMonth() / 12)
  const layerRef = useRef<HTMLDivElement | null>(null)
  const pillRef = useRef<HTMLSpanElement | null>(null)
  // Whether this overlay currently OWNS the pill (graph copy hidden under it).
  const shownRef = useRef(false)
  const animRef = useRef<Animation | null>(null)

  useLayoutEffect(() => {
    const layer = layerRef.current
    const pill = pillRef.current
    const scope = layer?.parentElement
    if (!layer || !pill || !scope) return
    const graphPill = scope.querySelector<HTMLElement>("[data-hud-year-pill]")
    if (!graphPill) return
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

    const poseOf = (el: HTMLElement) => {
      const r = el.getBoundingClientRect()
      const l = layer.getBoundingClientRect()
      return `translate(${(r.left - l.left).toFixed(1)}px, ${(r.top - l.top).toFixed(1)}px)`
    }
    // Mid-glide interruptions take off from the animated (visual) pose, not
    // the stale base target.
    const currentPose = () => {
      const anim = animRef.current
      if (anim && anim.playState === "running") return getComputedStyle(pill).transform
      return pill.style.transform || null
    }
    // Same for the tier morph: mid-glide the font-size/padding/radius are
    // partway between the tiers, so a reversal reads them off the computed
    // style rather than the stale base.
    const currentTier = (): PillTier | null => {
      const anim = animRef.current
      if (anim && anim.playState === "running") {
        const cs = getComputedStyle(pill)
        return { fontSize: cs.fontSize, padding: cs.padding, borderRadius: cs.borderRadius }
      }
      if (!pill.style.fontSize) return null
      return {
        fontSize: pill.style.fontSize,
        padding: pill.style.padding,
        borderRadius: pill.style.borderRadius,
      }
    }
    // The base transform + tier always hold the destination; the glide is a
    // WAAPI animation from wherever (and however-sized) the pill visually is
    // to that base.
    const glideTo = (pose: string, tier: PillTier, animate: boolean) => {
      const from = currentPose() ?? pose
      const fromTier = currentTier() ?? tier
      animRef.current?.cancel()
      pill.style.transform = pose
      Object.assign(pill.style, tier)
      if (animate && !reduced && (from !== pose || fromTier.fontSize !== tier.fontSize)) {
        animRef.current = pill.animate(
          [{ transform: from, ...fromTier }, { transform: pose, ...tier }],
          { duration: DOCK_MS, easing: DOCK_EASING },
        )
      }
    }

    let frame = 0
    const cleanups: Array<() => void> = []

    if (open) {
      // Take over from the graph pill on its exact pixels (and at its exact
      // tier), before paint.
      if (!shownRef.current) {
        pill.style.transform = poseOf(graphPill)
        Object.assign(pill.style, GRAPH_TIER)
        shownRef.current = true
      }
      graphPill.style.visibility = "hidden"
      pill.style.opacity = "1"

      let polls = 0
      const findSlotAndDock = () => {
        const slot = scope.querySelector<HTMLElement>("[data-metric-panel-year]")
        if (!slot) {
          if (polls++ < PLACEHOLDER_POLL_FRAMES) frame = requestAnimationFrame(findSlotAndDock)
          return
        }
        glideTo(poseOf(slot), PANEL_TIER, true)
        // Stay glued to the placeholder through panel scrolls and window
        // resizes (follows, not moves — no animation).
        const glue = () => glideTo(poseOf(slot), PANEL_TIER, false)
        const scroller = slot.closest<HTMLElement>('[role="dialog"]')
        scroller?.addEventListener("scroll", glue, { passive: true })
        window.addEventListener("resize", glue)
        cleanups.push(() => {
          scroller?.removeEventListener("scroll", glue)
          window.removeEventListener("resize", glue)
        })
      }
      frame = requestAnimationFrame(findSlotAndDock)
    } else if (shownRef.current) {
      // Glide home, then hand visibility back on the same pixels — keyed to
      // the glide's own finish event, so however the frame clock stretches,
      // the swap happens exactly when the pill has visibly arrived. A reopen
      // cancels the animation (glideTo), so a stale finish never fires.
      const pose = poseOf(graphPill)
      const from = currentPose() ?? pose
      const fromTier = currentTier() ?? GRAPH_TIER
      animRef.current?.cancel()
      pill.style.transform = pose
      Object.assign(pill.style, GRAPH_TIER)
      const handoff = () => {
        pill.style.opacity = "0"
        graphPill.style.visibility = ""
        shownRef.current = false
      }
      if (!reduced && (from !== pose || fromTier.fontSize !== GRAPH_TIER.fontSize)) {
        const anim = pill.animate(
          [{ transform: from, ...fromTier }, { transform: pose, ...GRAPH_TIER }],
          { duration: DOCK_MS, easing: DOCK_EASING },
        )
        animRef.current = anim
        anim.onfinish = handoff
        cleanups.push(() => {
          anim.onfinish = null
        })
      } else {
        handoff()
      }
    }

    return () => {
      cancelAnimationFrame(frame)
      for (const fn of cleanups) fn()
    }
  }, [open, openId])

  // Safety: never leave the graph's pill hidden if this overlay unmounts.
  useEffect(
    () => () => {
      const graphPill = document.querySelector<HTMLElement>("[data-hud-year-pill]")
      if (graphPill) graphPill.style.visibility = ""
    },
    [],
  )

  return (
    // Persistent overlay above both HUD layers; clips the pill once the
    // header (and the glued pill with it) scrolls out the panel's top.
    <div ref={layerRef} className="pointer-events-none absolute inset-0 z-[20] overflow-hidden" aria-hidden>
      {/* Font-size, padding and radius are set imperatively (GRAPH_TIER /
          PANEL_TIER above) — the glide animates between the tiers, so they
          can't be static classes. */}
      <span
        ref={pillRef}
        className="absolute left-0 top-0 inline-block bg-[#fffff8] font-mono font-bold text-black tabular-nums opacity-0"
      >
        <NumberFlow
          value={year}
          format={{ useGrouping: false }}
          transformTiming={TICKER_NUMBER_FLOW_TIMING}
          spinTiming={TICKER_NUMBER_FLOW_TIMING}
        />
      </span>
    </div>
  )
}
